import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { Activity, ActivityStatus, Client } from '../../../pure-js/types/GrooverTypes'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import { getActivitiesByCustomerId } from '../libs/DBApiHandler'
import { useMounted } from '../hooks/useMounted'
import Box from './Box'
import GrooverText from './GrooverText'

interface ActivitySuggestionListProps {
  client: Client
  /** Override text color for contexts with dark backgrounds (e.g. error boundary). Defaults to dark. */
  darkBackground?: boolean
}

const MAX_SUGGESTIONS = 5

const ActivitySuggestionList: React.FC<ActivitySuggestionListProps> = ({ client, darkBackground = false }) => {
  const isMounted = useMounted()
  const [activities, setActivities] = useState<Activity[]>([])

  useEffect(() => {
    const fetchActivities = async () => {
      const all = await getActivitiesByCustomerId(client.id, isMounted)
      if (!isMounted() || !all) return

      const cutoff = dayjs().subtract(21, 'days').startOf('day')
      const active = all
        .filter((a) => a.status === ActivityStatus.Active && dayjs(a.startDate).isAfter(cutoff))
        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
        .slice(0, MAX_SUGGESTIONS)

      setActivities(active)
    }

    fetchActivities()
  }, [client.id, isMounted])

  if (activities.length === 0) return null

  const titleColor = darkBackground ? 'white' : undefined
  const nameColor = darkBackground ? 'rgba(255,255,255,0.9)' : '#222'
  const dateColor = darkBackground ? 'rgba(255,255,255,0.5)' : '#888'
  const dividerColor = darkBackground ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'

  return (
    <Container top>
      <GrooverText textKey="activitySuggestionTitle" style={{ color: titleColor }} />
      <List>
        {activities.map((activity) => (
          <ListItem key={activity.id} $dividerColor={dividerColor}>
            <ActivityLink
              to={
                client.slug && activity.seoLink
                  ? buildRoute(RoutePath.ACTIVITY_DETAIL_SEO, {
                      clientSlug: client.slug,
                      activitySlug: activity.seoLink
                    })
                  : buildRoute(RoutePath.ACTIVITY_DETAIL, { activityId: activity.id })
              }
            >
              <ActivityName style={{ color: nameColor }}>{activity.name}</ActivityName>
              {activity.startDate && (
                <ActivityDate style={{ color: dateColor }}>
                  {dayjs(activity.startDate).format('D MMM YYYY')}
                </ActivityDate>
              )}
            </ActivityLink>
          </ListItem>
        ))}
      </List>
    </Container>
  )
}

const Container = styled(Box)`
  max-width: 480px;
  width: 100%;
  margin: 0 auto;
`

const List = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
`

const ListItem = styled.li<{ $dividerColor: string }>`
  border-bottom: 1px solid ${({ $dividerColor }) => $dividerColor};

  &:last-child {
    border-bottom: none;
  }
`

const ActivityLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  text-decoration: none;
  gap: 12px;

  &:hover {
    opacity: 0.8;
  }
`

const ActivityName = styled.span`
  font-size: 15px;
  font-weight: 500;
`

const ActivityDate = styled.span`
  font-size: 13px;
  white-space: nowrap;
`

export default ActivitySuggestionList
